import { logger } from '@/utils/logger';
import * as React from 'react';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { proxy, useSnapshot } from 'valtio';

type ConfirmationDialogProxy = {
  open: boolean;
  onOpenChange?: (open: boolean) => void;
  title: string;
  description: string;
  confirmText?: string;
  cancelText?: string;
  onConfirm: (() => void) | (() => Promise<void>);
  onCancel?: (() => void) | (() => Promise<void>);
  variant: 'default' | 'destructive';
  loading: boolean;
  /**
   * This flag determines whether the dialog closes after the onConfirm callback
   * has executed or immediately after the confirm button click, whenever the onConfirm
   * callback succeeds or not
   */
  confirmBehavior: 'wait-and-load' | 'close-immediately';
};

const defaultConfirmationDialogProxy: ConfirmationDialogProxy = {
  open: false,
  title: '',
  confirmBehavior: 'wait-and-load',
  description: '',
  confirmText: 'Confirm',
  cancelText: 'Cancel',
  onConfirm: () => {},
  variant: 'default',
  loading: false,
};
const confirmationDialogProxy = proxy<ConfirmationDialogProxy>(
  defaultConfirmationDialogProxy
);

export const useConfirmationDialog = () => {
  return useSnapshot(confirmationDialogProxy);
};

export const askConfirm = (props: Partial<typeof confirmationDialogProxy>) => {
  Object.assign(confirmationDialogProxy, { ...props, open: true });
};
export const closeConfirmationDialog = () => {
  confirmationDialogProxy.open = false;
  confirmationDialogProxy.onOpenChange =
    defaultConfirmationDialogProxy.onOpenChange;
  confirmationDialogProxy.title = '';
  confirmationDialogProxy.description = '';
  confirmationDialogProxy.confirmText = '';
  confirmationDialogProxy.cancelText = '';
  confirmationDialogProxy.onConfirm = defaultConfirmationDialogProxy.onConfirm;
  confirmationDialogProxy.onCancel = defaultConfirmationDialogProxy.onCancel;
  confirmationDialogProxy.variant = 'default';
  confirmationDialogProxy.loading = false;
};

export const ConfirmationDialog = React.memo(() => {
  const [isProcessing, setIsProcessing] = React.useState(false);
  const {
    open,
    onOpenChange,
    title,
    description,
    confirmText,
    cancelText,
    onConfirm,
    onCancel,
    variant,
    loading,
    confirmBehavior,
  } = useSnapshot(confirmationDialogProxy) as ConfirmationDialogProxy;

  const handleConfirm = async () => {
    if (confirmBehavior === 'wait-and-load') {
      setIsProcessing(true);
      try {
        await onConfirm?.();
        onOpenChange?.(false);
        confirmationDialogProxy.open = false;
      } catch (error) {
        logger.error('Confirmation action failed:', error);
      } finally {
        setIsProcessing(false);
      }
    } else {
      onConfirm?.();
      confirmationDialogProxy.open = false;
      onOpenChange?.(false);
    }
  };

  const handleCancel = () => {
    if (onCancel) {
      onCancel();
    }
    confirmationDialogProxy.open = false;
    onOpenChange?.(false);
  };

  const isDisabled = isProcessing || loading;

  return (
    <AlertDialog open={open} onOpenChange={onOpenChange}>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>{title}</AlertDialogTitle>
          <AlertDialogDescription>{description}</AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel onClick={handleCancel} disabled={isDisabled}>
            {cancelText}
          </AlertDialogCancel>
          <AlertDialogAction
            onClick={handleConfirm}
            disabled={isDisabled}
            className={
              variant === 'destructive'
                ? 'bg-destructive text-destructive-foreground hover:bg-destructive/90'
                : ''
            }
          >
            {isDisabled ? 'Processing...' : confirmText}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
});

ConfirmationDialog.displayName = 'ConfirmationDialog';
